<template>
  <transition name="fade">
    <div v-if="ui.showSidenav" class="fixed inset-0 z-40 bg-ink/60 backdrop-blur-sm xl:hidden" @click.self="ui.closeSidenav">
      <aside class="ml-auto flex h-full w-[min(88vw,380px)] flex-col overflow-y-auto bg-white shadow-2xl" aria-label="Mobile navigation">
        <div class="relative bg-gradient-to-r from-brand/10 via-white to-brand/5 p-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFE5EC] text-lg font-semibold text-[var(--accent)]">In</span>
              <div>
                <p class="text-lg font-semibold text-ink">Invi8</p>
                <p class="text-sm text-muted">{{ isLoggedIn ? displaySubtitle : 'Events & invites' }}</p>
              </div>
            </div>
            <button class="rounded-full border border-gray-200 bg-white p-2 shadow-sm transition hover:-translate-y-0.5 hover:border-brand hover:text-brand" @click="ui.closeSidenav" aria-label="Close menu">
              <i class="ri-close-line text-lg"></i>
            </button>
          </div>
          <div v-if="isLoggedIn" class="mt-4 rounded-3xl border border-white/70 bg-white/90 p-4 shadow-sm">
            <div class="flex items-center gap-3">
              <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFE5EC] text-brand">
                <i class="ri-user-3-line text-lg"></i>
              </span>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-semibold text-ink">{{ displayName }}</p>
                <p class="text-xs uppercase tracking-wide text-muted">{{ authRoleLabel }}</p>
              </div>
            </div>
            <div class="mt-4 grid grid-cols-2 gap-2 text-xs font-semibold">
              <RouterLink class="rounded-full border border-gray-200 bg-white px-3 py-2 text-center text-ink shadow-sm transition hover:border-brand hover:text-brand" :to="accountHomeLink" @click="ui.closeSidenav">{{ accountHomeLabel }}</RouterLink>
              <RouterLink class="rounded-full bg-brand px-3 py-2 text-center text-white shadow-sm transition hover:bg-brandDark" :to="accountSettingsLink" @click="ui.closeSidenav">Settings</RouterLink>
            </div>
            <button
              type="button"
              class="mt-3 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-center text-xs font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand"
              @click="handleLogout"
            >
              Logout
            </button>
          </div>
          <div v-else class="mt-4 grid grid-cols-2 gap-2 text-xs font-semibold">
            <RouterLink class="rounded-full border border-gray-200 bg-white px-3 py-2 text-center text-ink shadow-sm transition hover:border-brand hover:text-brand" to="/login" @click="ui.closeSidenav">Log in</RouterLink>
            <RouterLink class="rounded-full bg-brand px-3 py-2 text-center text-white shadow-sm transition hover:bg-brandDark" to="/for-vendors" @click="ui.closeSidenav">For vendors</RouterLink>
          </div>
        </div>

        <div class="flex-1 space-y-4 p-6 text-sm font-semibold text-ink">
          <div class="space-y-2">
            <p class="px-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Explore</p>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/" @click="ui.closeSidenav">Home</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/vendors" @click="ui.closeSidenav">Vendors</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/for-users" @click="ui.closeSidenav">For users</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/for-vendors" @click="ui.closeSidenav">Grow your business</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/venues" @click="ui.closeSidenav">Venues</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/categories" @click="ui.closeSidenav">Categories</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/use-cases" @click="ui.closeSidenav">Use Cases</RouterLink>
          </div>

          <div class="space-y-2">
            <p class="px-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Learn</p>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/about" @click="ui.closeSidenav">About</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/contact" @click="ui.closeSidenav">Contact</RouterLink>
          </div>

          <div class="space-y-2">
            <p class="px-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">Legal</p>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/privacy-policy" @click="ui.closeSidenav">Privacy Policy</RouterLink>
            <RouterLink class="block rounded-xl px-3 py-2 transition hover:bg-gray-50" to="/terms" @click="ui.closeSidenav">Terms & Conditions</RouterLink>
          </div>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { clearAuth, getAuthRole, getAuthUser, isAuthenticated } from '@/services/auth'

const ui = useUiStore()
const router = useRouter()
const isLoggedIn = ref(isAuthenticated())
const userProfile = ref(getAuthUser())
const authRole = ref(getAuthRole() || '')

const refreshAuthState = () => {
  isLoggedIn.value = isAuthenticated()
  userProfile.value = getAuthUser()
  authRole.value = getAuthRole() || ''
}

const accountHomeLink = computed(() => (authRole.value === 'vendor' ? '/dashboard' : '/host'))
const accountHomeLabel = computed(() => (authRole.value === 'vendor' ? 'Dashboard' : 'My Panel'))
const accountSettingsLink = computed(() => (authRole.value === 'vendor' ? '/dashboard/settings' : '/settings'))
const authRoleLabel = computed(() => (authRole.value === 'vendor' ? 'Vendor account' : 'User account'))
const displayName = computed(() => {
  const user = userProfile.value || {}
  return user.name || user.full_name || user.fullName || user.username || user.email || 'Account'
})
const displaySubtitle = computed(() => (authRole.value === 'vendor' ? 'Vendor workspace' : 'Your invitations & events'))

const handleEsc = (event) => {
  if (event.key === 'Escape') ui.closeSidenav()
}

const handleLogout = () => {
  clearAuth()
  refreshAuthState()
  ui.closeSidenav()
  router.push('/login')
}

const removeAfterEach = router.afterEach(() => ui.closeSidenav())

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    refreshAuthState()
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('keydown', handleEsc)
  refreshAuthState()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEsc)
  if (typeof removeAfterEach === 'function') removeAfterEach()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.flex-1 a.router-link-active {
  background: #fff7ed;
  color: #e56700;
}
</style>
